/**
 * Values Page
 *
 * Comprehensive values management with:
 * - Values Wheel visualization
 * - Values list with editing
 * - Auto-task creation for neglected values
 * - Guidance system
 */

import { useState } from 'react';
import { Plus, RefreshCw, AlertTriangle, Sparkles } from 'lucide-react';
import {
  useValues,
  useValuesStats,
  useCreateValue,
  useUpdateValue,
  useRecordEvidence,
  useCreateAutoTasks,
  useSeedValues,
} from '@/hooks/use-values';
import { useTask, useTaskMutations } from '@/hooks/use-tasks';
import { ValueCard, ValueEditModal, ValuesWheel, ValuesGuidance } from '@/components/values';
import { TaskEditModal } from '@/components/tasks/task-edit-modal';
import { Card, CardHeader, CardContent } from '@/components/ui';
import type { Value, CreateValueInput, UpdateValueInput } from '@/types/value';
import type { UpdateTaskInput } from '@/types';

type SortOption = 'priority' | 'congruence' | 'weight' | 'name';

export function ValuesPage() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingValue, setEditingValue] = useState<Value | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [showGuidance] = useState(true);
  
  // Task editing state
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const { data: editingTaskData } = useTask(editingTaskId || '');
  const editingTask = editingTaskData?.task || null;
  const { updateTask, completeTask, deleteTask } = useTaskMutations();

  const { data: valuesData, isLoading, refetch } = useValues(true);
  const { data: stats } = useValuesStats();

  const createValueMutation = useCreateValue();
  const updateValueMutation = useUpdateValue();
  const recordEvidenceMutation = useRecordEvidence();
  const autoTasksMutation = useCreateAutoTasks();
  const seedMutation = useSeedValues();

  const values = valuesData?.values || [];

  // Sort values
  const sortedValues = [...values].sort((a, b) => {
    switch (sortBy) {
      case 'priority':
        return (a.priority || 99) - (b.priority || 99);
      case 'congruence':
        return a.congruenceScore - b.congruenceScore;
      case 'weight':
        return b.weight - a.weight;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const handleEdit = (value: Value) => {
    setEditingValue(value);
    setIsEditModalOpen(true);
  };

  const handleCreate = () => {
    setEditingValue(null);
    setIsEditModalOpen(true);
  };

  const handleSave = async (input: CreateValueInput | UpdateValueInput) => {
    if (editingValue) {
      await updateValueMutation.mutateAsync({
        valueId: editingValue.id,
        input: input as UpdateValueInput,
      });
    } else {
      await createValueMutation.mutateAsync(input as CreateValueInput);
    }
    setIsEditModalOpen(false);
    setEditingValue(null);
  };

  const handleRecordEvidence = async (value: Value, type: 'lived' | 'violated') => {
    await recordEvidenceMutation.mutateAsync({
      valueId: value.id,
      input: { type },
    });
  };

  const handleCreateAutoTasks = async () => {
    const result = await autoTasksMutation.mutateAsync();
    if (result.tasksCreated.length > 0) {
      alert(`Created ${result.tasksCreated.length} tasks for neglected values!`);
    } else {
      alert('No new tasks needed - all neglected values already have tasks.');
    }
  };

  const handleSeedValues = async () => {
    const result = await seedMutation.mutateAsync();
    alert(`Seeded ${result.count} values!`);
  };

  // Task editing handlers
  const handleEditTask = (taskId: string) => {
    setEditingTaskId(taskId);
  };

  const handleSaveTask = async (taskId: string, updates: UpdateTaskInput) => {
    await updateTask.mutateAsync({ taskId, input: updates });
    setEditingTaskId(null);
  };

  const handleCompleteTask = async (taskId: string) => {
    await completeTask.mutateAsync(taskId);
    setEditingTaskId(null);
  };

  const handleDeleteTask = async (taskId: string) => {
    await deleteTask.mutateAsync(taskId);
    setEditingTaskId(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-stone-900">
            Values
          </h1>
          <p className="text-stone-600 mt-1">
            Your compass for living intentionally
          </p>
        </div>
        <div className="flex items-center gap-2">
          {values.length === 0 && (
            <button
              onClick={handleSeedValues}
              disabled={seedMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Load Example Values
            </button>
          )}
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Value
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-2xl font-bold text-stone-900">{stats.total}</div>
            <div className="text-sm text-stone-500">Total Values</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-stone-900">
              {Math.round(stats.avgCongruence * 100)}%
            </div>
            <div className="text-sm text-stone-500">Avg Alignment</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.strong}</div>
            <div className="text-sm text-stone-500">Strong</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.partial}</div>
            <div className="text-sm text-stone-500">Partial</div>
          </Card>
          <Card className="p-4 flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-red-600">{stats.gap}</div>
              <div className="text-sm text-stone-500">Need Attention</div>
            </div>
            {stats.gap > 0 && (
              <button
                onClick={handleCreateAutoTasks}
                disabled={autoTasksMutation.isPending}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Create tasks for neglected values"
              >
                <AlertTriangle className="w-5 h-5" />
              </button>
            )}
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column: Wheel + Guidance */}
        <div className="lg:col-span-1 space-y-6">
          {/* Values Wheel */}
          <Card className="p-6">
            <h2 className="font-display font-semibold text-stone-900 mb-4">Values Wheel</h2>
            <div className="flex justify-center pb-8">
              <ValuesWheel
                values={sortedValues}
                size={280}
                onValueClick={handleEdit}
              />
            </div>
            <p className="text-xs text-stone-500 text-center mt-4">
              Slice size = weight percentage. Color = alignment score.
            </p>
          </Card>

          {/* Guidance Panel */}
          {showGuidance && (
            <ValuesGuidance />
          )}
        </div>

        {/* Right Column: Values List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="border-b border-stone-100">
              <div className="flex items-center justify-between">
                <h2 className="font-display font-semibold text-stone-900">
                  All Values ({values.length})
                </h2>
                <div className="flex items-center gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="text-sm border border-stone-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="priority">Sort by Priority</option>
                    <option value="congruence">Sort by Alignment</option>
                    <option value="weight">Sort by Weight</option>
                    <option value="name">Sort by Name</option>
                  </select>
                  <button
                    onClick={() => refetch()}
                    className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-stone-100 rounded-xl h-32" />
                  ))}
                </div>
              ) : values.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-stone-400" />
                  </div>
                  <h3 className="font-semibold text-stone-900 mb-2">No values yet</h3>
                  <p className="text-stone-500 mb-4">
                    Start by adding your core values or loading example values.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={handleSeedValues}
                      disabled={seedMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors"
                    >
                      Load Examples
                    </button>
                    <button
                      onClick={handleCreate}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      Add Value
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedValues.map((value) => (
                    <ValueCard
                      key={value.id}
                      value={value}
                      onEdit={handleEdit}
                      onRecordEvidence={handleRecordEvidence}
                      onEditTask={handleEditTask}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Modal */}
      <ValueEditModal
        value={editingValue}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingValue(null);
        }}
        onSave={handleSave}
        isLoading={createValueMutation.isPending || updateValueMutation.isPending}
      />

      {/* Task Edit Modal */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTaskId}
          onClose={() => setEditingTaskId(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          onComplete={handleCompleteTask}
          onDismiss={handleDeleteTask}
        />
      )}
    </div>
  );
}

